import {AppCtx} from 'context/AppContext';
import {useContext} from 'react';
import {errorDataHelper, usePostError} from 'hooks';
import {isObject} from 'utils/objects';
import {useHistory} from 'react-router-dom';
import {FormState} from 'components/forms/context';

const successReducer = (prevResults, thisObj) =>
    prevResults && thisObj?.status === 'ok';

const useOnSubmitResponse = ({
    node,
    // success
    showSuccessSnack = true,
    successText = 'Form saved!',
    clearFormState,
    successFunction,
    redirectOnSuccess = true,
    redirectUrl = '/home',
    checkSuccess,
    // error
    showErrorSnack = true,
    errorText = 'Something went wrong with our system. We were notified and are working on it. Please try again later',
    errorMap,
    logErrorInFeedback = true,
    errorFunction,
}) => {
    /*
    inputs:
    - node: string => used to identify the form in event of an error
    // success
    - showSuccessSnack: boolean => display the snackbar if result is successful
    - successText: string => text to display on success
    - clearFormState: function => if present will run as state cleanup on success
    - successFunction: function => if present will run after successful response.
    - redirectOnSuccess: boolean => if true will force a page redirct to redirectUrl
    - redirectUrl: string => default redirect on success.
    - checkSuccess: function => optional function to check if result is successful
    // error
    - showErrorSnack: boolean => show snacbar message on error
    - errorText: string => message text to show on error
    - errorMap: map => if the errorMap has a key equal to the error message, it will show the message value
    - logErrorInFeedback: boolean => send the error to the /feedback endpoint 
    - errorFunction: function => if present will run after request fails.

    outputs:
    - onSubmitResponse: function => function to call with response or error from server
    */

    const {setSnackProps, setSnackOpen} = useContext(AppCtx);
    const history = useHistory();
    const {post: postError} = usePostError();
    const {node: contextNode} = useContext(FormState);

    const onSuccess = result => {
        if (showSuccessSnack) {
            setSnackProps({
                severity: 'success',
                message: successText,
            });
            setSnackOpen(true);
        }
        clearFormState && clearFormState();
        successFunction && successFunction(result);
        redirectOnSuccess && history.push(redirectUrl);
    };
    const onError = error => {
        console.debug('error', error);
        if (showErrorSnack) {
            setSnackOpen(true);
            let message = errorText;
            if (errorMap) {
                if (errorMap.has(error?.message)) {
                    message = errorMap.get(error.message);
                }
            }
            setSnackProps({
                severity: 'warning',
                message: message,
            });
        }
        if (logErrorInFeedback) {
            postError(
                errorDataHelper({
                    feedback_text: `[AUTO GENERATED][${
                        node || contextNode
                    } FORM SUBMIT ERROR] Result from server: ${JSON.stringify(
                        error,
                        null,
                        2
                    )}`,
                })
            );
        }
        errorFunction && errorFunction();
    };

    const onSubmitResponse = ({result, error}) => {
        /*
        a helper function for handling redirects, messages, and logging in the event of
        response from from and endpoint (usually after posting data)

        inputs:
        - result: result object returned from endpoint
        - error: error object returned from endpoint

        outputs:
        none. result or error status will invoke above logic, must of the time showing snackbar of result
        */
        if (
            Array.isArray(result) &&
            result.reduce((a, b) => a && isObject(b))
        ) {
            result.reduce(successReducer) ? onSuccess() : onError(result);
        }
        if (isObject(result)) {
            if (
                result?.status === 'ok' ||
                (checkSuccess && checkSuccess(result))
            ) {
                onSuccess();
            } else {
                onError(result);
            }
        }
        if (error) {
            onError(error);
        }
    };
    return onSubmitResponse;
};

export default useOnSubmitResponse;
